<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue';
import { WhatsappCodeService } from '@/services';
import { toastServiceError } from '@/services/core/notification';
import { useUiStore } from '@/stores';
import { NumberModel, WhatsappCodeServiceGetNumbersParams } from '@/types';
import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';
import WhatsappCodeNumberListItem from './components/WhatsappCodeNumberListItem.vue';

const numbers = ref<NumberModel[]>([]);
const count = ref(0);
const perPage = 10;
const currentPage = ref(1);
const search = ref('');

const uiStore = useUiStore();
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getParams = (): WhatsappCodeServiceGetNumbersParams => ({
    page: currentPage.value,
    per_page: perPage,
    search: search.value || undefined,
});

const fetchNumbers = async () => {
    setLoading(true);
    try {
        const { data, headers } = await WhatsappCodeService.getNumbers({
            params: getParams(),
        });
        numbers.value = data;
        count.value = parseInt(headers['x-pagination-count']) || data.length;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

watch([currentPage], () => {
    fetchNumbers();
});

watch([search], () => {
    if (currentPage.value === 1) fetchNumbers();
    else currentPage.value = 1;
});

onMounted(() => {
    fetchNumbers();
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="flex items-center text-xl font-semibold text-gray-800">
                    <SVGWhatsApp class="mr-1.5 h-6 w-6 text-[#25D366]" />
                    {{ $t('whatsappCode.title') }}
                </h1>
            </template>
        </UiPageHeader>

        <div class="space-y-2">
            <UiPanel class="flex px-4 py-2" no-padding>
                <UiListSearch
                    v-model="search"
                    :placeholder="$t('whatsappCode.searchPlaceholder')"
                    class="flex-1"
                />
            </UiPanel>

            <div v-if="numbers.length" class="space-y-2">
                <WhatsappCodeNumberListItem
                    v-for="number in numbers"
                    :key="number.id"
                    :model-value="number"
                />
            </div>
            <UiPanel v-else class="flex items-center justify-center py-8 text-gray-500">
                {{ $t('core.messages.noRecordsFound') }}
            </UiPanel>

            <UiPagination
                v-if="numbers.length"
                v-model="currentPage"
                :count="count"
                :perPage="perPage"
            />
        </div>
    </div>
</template>
